import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Flame, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido a ZAE Ejecutivo");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-slate-950/92 to-black/85" />
      <div className="absolute inset-0 grid-texture opacity-40" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl bg-slate-900/85 backdrop-blur-xl border border-red-900/40 p-8 sm:p-10 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-red-900/50 mb-4 p-2.5">
            <img src="/logo-zae.png" alt="ZAE Ejecutivo" className="w-full h-full object-contain" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-white tracking-tight">
            Grupo Tomza
          </h1>
          <p className="text-red-500 text-xs uppercase tracking-[0.3em] font-semibold mt-1">
            ZAE Ejecutivo
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm">Correo electrónico</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@tomza.com"
              required
              data-testid="login-email-input"
              className="mt-1.5 bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-red-600"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Contraseña</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-red-600 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                data-testid="toggle-password-visibility"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-11 shadow-lg shadow-red-900/40"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Iniciar sesión"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
